import * as React from 'react';
import { withStyles, Theme, WithStyles, createStyles } from '@material-ui/core';
import { InputProps } from '@material-ui/core/Input';

import units from 'ha/theme/units';
import { Mix } from 'ha/utils/reduxForm/muiHelpers';
import Locations, { MIN_SEARCH_INPUT_LENGTH } from 'ha/modules/Locations';
import { PredictionType } from 'ha/types/GoogleMaps';
import InputAdornment from 'ha/ui/next/InputAdornment';
import SvgIcon from 'ha/ui/next/SvgIcon';
import IconButton from 'ha/ui/next/IconButton';
import NavigationIcon from 'streamline-icons/navigation.svg';
import CloseIcon from 'streamline-icons/cross-symbol.svg';

import { SimpleItem } from 'ha/components/next/LocationAutocompleteInput';
import SearchBarTextField from './SearchBarTextField';

interface Props {
  id: string;
  label?: string;
  dataTestLocator?: string;
  disabled?: boolean;
  predictionTypes: PredictionType[];
  name: string;
  required: boolean;
  placeholder?: string;
  initialItems: SimpleItem[];
}

export const styles = (theme: Theme) =>
  createStyles({
    icon: {
      marginLeft: units.rem(1),
      marginRight: units.rem(-0.5),
    },
    input: {
      fontWeight: 'bold',
      [theme.breakpoints.up('md')]: {
        fontSize: units.rem(2),
        lineHeight: 2,
      },
    },
  });

export class LocationsInput extends React.Component<
  Mix<Props> & WithStyles<typeof styles>
> {
  state = {
    renderInitialItems: false,
  };

  handleFocus = (value: string) => {
    // Show initial suggestions on input focus
    if (this.props.input.value === '' || value === '') {
      this.setState({ renderInitialItems: true });
    }
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // User erased all symbols, lets show initial suggestions
    if (e.target.value.length === 0) {
      this.setState({ renderInitialItems: true });
    }

    // User typed MIN_SEARCH_INPUT_LENGTH letters
    // lets hide initial suggestions and show ones from google
    if (
      e.target.value.length >= MIN_SEARCH_INPUT_LENGTH &&
      this.state.renderInitialItems === true
    ) {
      this.setState({ renderInitialItems: false });
    }
  };

  handleSelect = () => {
    this.setState({ renderInitialItems: false });
  };

  render() {
    const {
      input,
      meta,
      predictionTypes,
      label = '',
      id,
      dataTestLocator,
      disabled,
      name,
      placeholder,
      required,
      classes,
      initialItems,
    } = this.props;

    const { renderInitialItems } = this.state;

    return (
      <Locations
        renderInput={inputProps => {
          // we extract the onBlur from InputProps because
          // if we pass down the event to the input directly
          // form validation does not work as expected
          // it will never render error
          // so we pass onBlur to TextField making validation work
          // and now we are able to select items with tab
          const { ref, onBlur, onFocus, onChange, ...other } = inputProps;
          return (
            <SearchBarTextField
              {...input}
              fullWidth
              name={name}
              InputProps={{
                inputRef: ref as React.RefObject<HTMLInputElement>,
                startAdornment: (
                  <InputAdornment position="start" className={classes.icon}>
                    <SvgIcon>
                      <NavigationIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: input.value && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        input.onChange('');
                      }}
                    >
                      <SvgIcon>
                        <CloseIcon />
                      </SvgIcon>
                    </IconButton>
                  </InputAdornment>
                ),
                ...(other as InputProps),
                classes: {
                  input: classes.input,
                },
              }}
              onBlur={onBlur}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (onChange) {
                  this.handleChange(e);
                  onChange(e);
                }
              }}
              onFocus={e => {
                if (onFocus) {
                  this.handleFocus(input.value);
                  onFocus(e);
                }
              }}
              id={id}
              label={label}
              disabled={disabled}
              required={required}
              placeholder={placeholder}
              data-test-locator={dataTestLocator}
              error={meta.touched && meta.error}
              helperText={meta.touched && meta.error}
            />
          );
        }}
        value={input.value}
        predictionTypes={predictionTypes}
        onSelect={value => {
          this.handleSelect();
          input.onChange(value);
        }}
        onChange={input.onChange}
        renderInitialItems={renderInitialItems}
        initialItems={initialItems}
      />
    );
  }
}

export default withStyles(styles)(LocationsInput);
